import React, { useState, useEffect } from 'react';

/**
 * Composant InstallPrompt - Affiche un prompt pour installer l'application PWA
 */
const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Vérifier si l'app est déjà installée
    const checkInstalled = () => {
      const installed = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
      setIsInstalled(installed);
      
      if (installed) {
        console.log('[PWA] Application déjà installée');
      }
    };

    checkInstalled();

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt déclenché');
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Afficher le prompt après 5 secondes (ou selon votre logique)
      setTimeout(() => {
        if (!isInstalled) {
          setShowPrompt(true);
        }
      }, 5000);
    };

    // Écouter l'installation de l'app
    const handleAppInstalled = () => {
      console.log('[PWA] Application installée avec succès !');
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Afficher le prompt d'installation natif
    deferredPrompt.prompt();

    // Attendre la réponse de l'utilisateur
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[PWA] Choix utilisateur:', outcome);

    if (outcome === 'accepted') {
      console.log('[PWA] Utilisateur a accepté l\'installation');
    } else {
      console.log('[PWA] Utilisateur a refusé l\'installation');
    }

    // Reset
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    
    // Ne pas afficher de nouveau pendant 7 jours
    localStorage.setItem('fsf-install-prompt-dismissed', Date.now().toString());
  };

  // Ne rien afficher si l'app est installée ou si le prompt n'est pas disponible
  if (isInstalled || !showPrompt || !deferredPrompt) {
    return null;
  }

  // Vérifier si l'utilisateur a récemment dismissé le prompt
  const dismissedTime = localStorage.getItem('fsf-install-prompt-dismissed');
  if (dismissedTime) {
    const daysSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) {
      return null;
    }
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-sm bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-50 animate-slide-up">
      <div className="flex items-start gap-3">
        {/* Icône de l'app */}
        <div className="flex-shrink-0">
          <img 
            src="/logo192.png" 
            alt="FSF Logo" 
            className="w-12 h-12 rounded-lg"
          />
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Installer FSF
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Installez l'application sur votre appareil pour un accès rapide et une expérience optimale, même hors ligne.
          </p>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200"
            >
              Installer
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors duration-200"
            >
              Plus tard
            </button>
          </div>
        </div>

        {/* Bouton fermer */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Indicateur d'animation */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InstallPrompt;
